import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getdiscrepancyDetails } from "@tKgpco/Services/DiscrepancyService";
import { getRMADetails } from "@tKgpco/Services/RMAService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";
import { loadDiscrepancyDetailsCollection } from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyDetailsCollection";
import loadRMADetailsCollection from "@tKgpco/Store/Data/RMA/Handlers/loadRMADetailsCollection";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    let rmaNumber;

    if (location && location.search) {
        const parsedQuery = parseQueryStringCaseInsensitive(location.search, {
            rmaNumber: "",
        });
        rmaNumber = parsedQuery.rmaNumber;
    }

    return {
        userName: state.context.session.userName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isLoggedIN: state.context.session.isAuthenticated,
        discrepancyStore: state.KGPState.DiscrepancyStore,
        parameters: state.KGPState.DiscrepancyStore.discrepancyCollectionParams,
        location,
        rmaNumber,
        featurePages: state.context.session.properties.featurePages,
        isSecurityRolesAssigned: state.context.session?.properties?.isSecurityRolesAssigned?.toLowerCase() === "true",
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    loadDiscrepancyCollection,
    loadDiscrepancyDetailsCollection,
    loadRMADetailsCollection,
};

export interface RMADetailsContainerStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
}

export const RMADetailsContainerStyles: RMADetailsContainerStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    container: {
        css: css`
            @media print {
                .rma-details-wrapper {
                    > div > :first-child {
                        width: 100%;
                    }
                    > div > :nth-child(2) {
                        width: 100%;
                    }
                }
            }
        `,
    },
};

const styles = RMADetailsContainerStyles;

const RMADetailsContainer: React.FC<Props> = ({
    id,
    userName,
    signInUrl,
    currentUserIsGuest,
    history,
    rmaNumber,
    loadRMADetailsCollection,
    featurePages,
    isSecurityRolesAssigned,
}) => {
    useEffect(() => {
        if (!(userName && !currentUserIsGuest)) {
            history.push(`${signInUrl}`);
        }
    }, [userName, currentUserIsGuest]);

    useEffect(() => {
        if (rmaNumber && userName && !currentUserIsGuest) {
            loadRMADetailsCollection({ rmaNumber });
        }
    }, [rmaNumber, userName, currentUserIsGuest]);

    if (featurePages?.includes("RMA Details")) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="no-access-message">
                    {translate(`You don’t have permission to view this page`)}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.container}>
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RMADetailsContainer)),
    definition: {
        group: "Common",
        displayName: "RMADetailsContainer",
        fieldDefinitions: [],
    },
};

export default widgetModule;
